import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Alert, Spinner, Modal } from 'react-bootstrap';
import { 
    Download, CheckCircle2, AlertTriangle, Car, MapPin, 
    Clock, RefreshCw, FileSpreadsheet, Sparkles 
} from 'lucide-react';
import { toast } from 'react-toastify';
import AddressAutocomplete from './AddressAutocomplete';
import RouteMap from './RouteMap';
import { 
    getClients, getRequesters, getAreas, getVehicleTypes,
    downloadSmartBulkTemplate, previewSmartBulk, confirmSmartBulk 
} from '../api';

const OFFICIAL_CAPACITIES = {
    'STANDARD': 2,
    'EXECUTIVE': 3,
    'LUXURY': 3,
    'PEOPLE_CARRIER': 5,
    'LARGE_PEOPLE_CARRIER': 6,
    'EXECUTIVE_PEOPLE_CARRIER': 5,
    'MINIBUS': 10,
    'Minibus 12 Pax': 12,
    'Sprinter': 21,
    'Minibus 24': 24,
    'BUS': 44
};

const OFFICIAL_VEHICLE_DEFAULTS = [
    { type: 'STANDARD', name: 'Standard (2 pax)', count: 0, capacity: 2 },
    { type: 'EXECUTIVE', name: 'Executive (3 pax)', count: 0, capacity: 3 },
    { type: 'LUXURY', name: 'Luxury (3 pax)', count: 0, capacity: 3 },
    { type: 'PEOPLE_CARRIER', name: 'People Carrier (5 pax)', count: 0, capacity: 5 },
    { type: 'LARGE_PEOPLE_CARRIER', name: 'Large People Carrier (6 pax)', count: 0, capacity: 6 },
    { type: 'EXECUTIVE_PEOPLE_CARRIER', name: 'Executive People Carrier (5 pax)', count: 0, capacity: 5 },
    { type: 'MINIBUS', name: 'Minibus (10 pax)', count: 0, capacity: 10 },
    { type: 'Minibus 12 Pax', name: 'Minibus 12 Pax (12 pax)', count: 0, capacity: 12 },
    { type: 'Sprinter', name: 'Sprinter (21 pax)', count: 0, capacity: 21 },
    { type: 'Minibus 24', name: 'Minibus 24 (24 pax)', count: 0, capacity: 24 },
    { type: 'BUS', name: 'Bus (44 pax)', count: 0, capacity: 44 }
];

export default function SmartBulkLoadView() {
    // Listados de selección
    const [clients, setClients] = useState([]);
    const [requesters, setRequesters] = useState([]);
    const [areas, setAreas] = useState([]);

    // Estado del Formulario Superior
    const [selectedClientId, setSelectedClientId] = useState('');
    const [selectedRequesterId, setSelectedRequesterId] = useState('');
    const [selectedAreaId, setSelectedAreaId] = useState('');
    const [operationType, setOperationType] = useState('zarpe'); // 'zarpe' | 'recogida'
    const [commonAddress, setCommonAddress] = useState('');
    const [commonCoords, setCommonCoords] = useState({ lat: null, lng: null });
    const [toleranceMinutes, setToleranceMinutes] = useState(30);
    const [vehicleMix, setVehicleMix] = useState(OFFICIAL_VEHICLE_DEFAULTS);
    const [observations] = useState('');
    const [uploadedFile, setUploadedFile] = useState(null);

    // Estados de Carga y Previsualización
    const [processingPreview, setProcessingPreview] = useState(false);
    const [previewResult, setPreviewResult] = useState(null);
    const [confirming, setConfirming] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState('cl'); // 'cl' | 'pe' | 'ar' | 'co'
    const [selectedRouteVeh, setSelectedRouteVeh] = useState(null);
    const [editingPaxId, setEditingPaxId] = useState(null); // temp_id del pasajero editando dirección en vivo
    const [draggedIndex, setDraggedIndex] = useState(null); // índice para Drag & Drop de paradas
    const [routeStats, setRouteStats] = useState({ distKm: 0, durationMins: 0 }); // Estadísticas de tiempo y distancia de la ruta actual

    // Cargar listas iniciales (Clientes y Tipos de Vehículos oficiales)
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const [clientsRes, vTypesRes] = await Promise.all([
                    getClients(),
                    getVehicleTypes().catch(() => [])
                ]);
                setClients(clientsRes || []);

                if (vTypesRes && vTypesRes.length > 0) {
                    const loadedMix = vTypesRes.map(vt => {
                        const vName = vt.name || vt.category || vt.code;
                        const realCap = vt.capacity || OFFICIAL_CAPACITIES[vName] || 4;
                        return {
                            type: vName,
                            name: `${vName} (${realCap} pax)`,
                            count: 0,
                            capacity: realCap
                        };
                    });
                    setVehicleMix(loadedMix);
                }
            } catch (err) {
                console.error("Error al cargar datos iniciales:", err);
                toast.error("No se pudieron cargar los tipos de vehículos.");
            }
        };
        loadInitialData();
    }, []);

    // Cargar solicitantes y áreas al cambiar cliente
    useEffect(() => {
        if (!selectedClientId) {
            setRequesters([]);
            setAreas([]);
            setSelectedRequesterId('');
            setSelectedAreaId('');
            return;
        }
        const fetchDependencies = async () => {
            try {
                const [reqsRes, areasRes] = await Promise.all([
                    getRequesters({ client_id: selectedClientId }),
                    getAreas({ client_id: selectedClientId })
                ]);
                setRequesters(reqsRes || []);
                setAreas(areasRes || []);
            } catch (err) {
                console.error("Error al cargar solicitantes/áreas:", err);
            }
        };
        fetchDependencies();
    }, [selectedClientId]);

    // Modificar cantidad de vehículos en el Mix
    const handleVehicleCountChange = (vType, delta) => {
        setVehicleMix(prev => prev.map(v => {
            if (v.type === vType) {
                const newCount = Math.max(0, v.count + delta);
                return { ...v, count: newCount };
            }
            return v;
        }));
    };

    // Descargar Plantilla Modelo
    const handleDownloadTemplate = async () => {
        try {
            const blobData = await downloadSmartBulkTemplate();
            const blob = new Blob([blobData], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Plantilla_Carga_Inteligente_EmiGo.xlsx');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            toast.success("Plantilla modelo descargada.");
        } catch (err) {
            console.error("Error al descargar plantilla:", err);
            toast.error("Error al descargar la plantilla.");
        }
    };

    // Procesar Previsualización
    const handleProcessPreview = async (e) => {
        e.preventDefault();
        if (!selectedClientId) {
            toast.warning("Debe seleccionar un Cliente Corporativo.");
            return;
        }
        if (!uploadedFile) {
            toast.warning("Debe seleccionar una planilla Excel/CSV.");
            return;
        }
        if (!commonAddress) {
            toast.warning("Debe especificar la dirección del Punto Común.");
            return;
        }

        const totalVehiclesCount = vehicleMix.reduce((sum, v) => sum + v.count, 0);
        if (totalVehiclesCount === 0) {
            toast.warning("Debe seleccionar al menos 1 vehículo en el Mix.");
            return;
        }

        setProcessingPreview(true);
        setPreviewResult(null);

        try {
            const formData = new FormData();
            formData.append('client_id', selectedClientId);
            if (selectedRequesterId) formData.append('requester_id', selectedRequesterId);
            if (selectedAreaId) formData.append('area_id', selectedAreaId);
            formData.append('operation_type', operationType);
            formData.append('country_code', selectedCountry);
            formData.append('common_address', commonAddress);
            if (commonCoords.lat) formData.append('common_lat', commonCoords.lat);
            if (commonCoords.lng) formData.append('common_lng', commonCoords.lng);
            formData.append('tolerance_minutes', toleranceMinutes);

            const mixObj = {};
            vehicleMix.forEach(v => {
                if (v.count > 0) mixObj[v.type] = v.count;
            });
            formData.append('vehicle_mix_json', JSON.stringify(mixObj));
            if (observations) formData.append('observations', observations);
            formData.append('file', uploadedFile);

            const result = await previewSmartBulk(formData);
            setPreviewResult(result);
            toast.success("Previsualización calculada con éxito.");
        } catch (err) {
            console.error("Error en previsualización:", err);
        } finally {
            setProcessingPreview(false);
        }
    };

    // Mover Pasajero entre Vehículos
    const handleMovePassenger = (paxTempId, targetSlotId) => {
        if (!previewResult) return;
        setPreviewResult(prev => {
            let foundPax = null;

            // Extraer pasajero de su vehículo actual o de sin asignar
            const updatedVehicles = prev.vehicles.map(v => {
                const filteredPax = v.passengers.filter(p => {
                    if (p.temp_id === paxTempId) {
                        foundPax = p;
                        return false;
                    }
                    return true;
                });
                return { ...v, passengers: filteredPax };
            });

            const updatedUnassigned = prev.unassigned_passengers.filter(p => {
                if (p.temp_id === paxTempId) {
                    foundPax = p;
                    return false;
                }
                return true;
            });

            if (!foundPax) return prev;

            if (targetSlotId === 'unassigned') {
                return {
                    ...prev,
                    vehicles: updatedVehicles,
                    unassigned_passengers: [...updatedUnassigned, foundPax]
                };
            } else {
                const targetVehSlot = Number(targetSlotId);
                const finalVehicles = updatedVehicles.map(v => {
                    if (v.vehicle_slot_id === targetVehSlot) {
                        return { ...v, passengers: [...v.passengers, foundPax] };
                    }
                    return v;
                });
                return {
                    ...prev,
                    vehicles: finalVehicles,
                    unassigned_passengers: updatedUnassigned
                };
            }
        });
        toast.info("Pasajero reasignado de vehículo.");
    };

    // Reordenar Parada de Pasajero dentro del Vehículo (Mover Arriba ⬆️ / Bajar ⬇️)
    const handleReorderPassenger = (vehSlotId, index, direction) => {
        if (!previewResult) return;
        setPreviewResult(prev => {
            const vehiclesCopy = [...prev.vehicles];
            const vehIndex = vehiclesCopy.findIndex(v => v.vehicle_slot_id === vehSlotId);
            if (vehIndex === -1) return prev;

            const targetVeh = { ...vehiclesCopy[vehIndex] };
            const paxList = [...targetVeh.passengers];
            const targetIndex = index + direction;

            if (targetIndex < 0 || targetIndex >= paxList.length) return prev;

            // Intercambiar posiciones
            const temp = paxList[index];
            paxList[index] = paxList[targetIndex];
            paxList[targetIndex] = temp;

            targetVeh.passengers = paxList;

            // Reconstruir el itinerario del vehículo
            const paxAddresses = paxList.map(p => p.address).filter(Boolean);
            if (operationType === 'zarpe') {
                targetVeh.itinerary = {
                    origin: commonAddress,
                    destination: paxAddresses[paxAddresses.length - 1] || commonAddress,
                    waypoints: paxAddresses.slice(0, -1)
                };
            } else {
                targetVeh.itinerary = {
                    origin: paxAddresses[0] || commonAddress,
                    destination: commonAddress,
                    waypoints: paxAddresses.slice(1)
                };
            }

            vehiclesCopy[vehIndex] = targetVeh;

            if (selectedRouteVeh && selectedRouteVeh.vehicle_slot_id === vehSlotId) {
                setSelectedRouteVeh(targetVeh);
            }

            return { ...prev, vehicles: vehiclesCopy };
        });
        toast.info("Secuencia de paradas actualizada.");
    };

    // Formatear Duración en Horas y Minutos (ej. 2 h 10 min)
    const formatDuration = (mins) => {
        if (!mins || mins <= 0) return 'Calculando...';
        const hrs = Math.floor(mins / 60);
        const m = mins % 60;
        return hrs > 0 ? `${hrs} h ${m} min` : `${m} min`;
    };

    // Reordenar Parada por Arrastrar y Soltar (Drag & Drop)
    const handleReorderPassengerDrag = (vehSlotId, fromIndex, toIndex) => {
        if (!previewResult || fromIndex === toIndex || fromIndex === null || toIndex === null) return;
        setPreviewResult(prev => {
            const vehiclesCopy = [...prev.vehicles];
            const vehIndex = vehiclesCopy.findIndex(v => v.vehicle_slot_id === vehSlotId);
            if (vehIndex === -1) return prev;

            const targetVeh = { ...vehiclesCopy[vehIndex] };
            const paxList = [...targetVeh.passengers];

            const [movedPax] = paxList.splice(fromIndex, 1);
            paxList.splice(toIndex, 0, movedPax);

            targetVeh.passengers = paxList;

            // Reconstruir el itinerario del vehículo
            const paxAddresses = paxList.map(p => p.address).filter(Boolean);
            if (operationType === 'zarpe') {
                targetVeh.itinerary = {
                    origin: commonAddress,
                    destination: paxAddresses[paxAddresses.length - 1] || commonAddress,
                    waypoints: paxAddresses.slice(0, -1)
                };
            } else {
                targetVeh.itinerary = {
                    origin: paxAddresses[0] || commonAddress,
                    destination: commonAddress,
                    waypoints: paxAddresses.slice(1)
                };
            }

            vehiclesCopy[vehIndex] = targetVeh;

            if (selectedRouteVeh && selectedRouteVeh.vehicle_slot_id === vehSlotId) {
                setSelectedRouteVeh(targetVeh);
            }

            return { ...prev, vehicles: vehiclesCopy };
        });
        toast.info("Secuencia de paradas reordenada.");
    };

    // Dividir Vehículo en 2
    const handleSplitVehicle = (slotId) => {
        if (!previewResult) return;
        setPreviewResult(prev => {
            const targetVehIndex = prev.vehicles.findIndex(v => v.vehicle_slot_id === slotId);
            if (targetVehIndex === -1) return prev;

            const targetVeh = prev.vehicles[targetVehIndex];
            if (targetVeh.passengers.length <= 1) {
                toast.warning("El vehículo debe tener al menos 2 pasajeros para dividirse.");
                return prev;
            }

            const half = Math.ceil(targetVeh.passengers.length / 2);
            const paxGroup1 = targetVeh.passengers.slice(0, half);
            const paxGroup2 = targetVeh.passengers.slice(half);

            const newSlotId = Math.max(...prev.vehicles.map(v => v.vehicle_slot_id), 0) + 1;
            const updatedTargetVeh = { ...targetVeh, passengers: paxGroup1 };
            const newVeh = {
                ...targetVeh,
                vehicle_slot_id: newSlotId,
                passengers: paxGroup2
            };

            const newVehicles = [...prev.vehicles];
            newVehicles[targetVehIndex] = updatedTargetVeh;
            newVehicles.splice(targetVehIndex + 1, 0, newVeh);

            toast.info(`Vehículo #${slotId} dividido en Vehículo #${newSlotId}`);
            return { ...prev, vehicles: newVehicles };
        });
    };

    // Cálculo Haversine local para actualizar distancia en caliente al corregir dirección
    const haversineKm = (lat1, lon1, lat2, lon2) => {
        if (!lat1 || !lon1 || !lat2 || !lon2) return 0;
        const R = 6371;
        const dLat = (Number(lat2) - Number(lat1)) * (Math.PI / 180);
        const dLon = (Number(lon2) - Number(lon1)) * (Math.PI / 180);
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                  Math.cos(Number(lat1) * (Math.PI / 180)) * Math.cos(Number(lat2) * (Math.PI / 180)) *
                  Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    };

    // Guardar Dirección Corregida mediante Google Maps Autocomplete
    const handleSaveCorrectedAddress = (paxTempId, locationObj) => {
        if (!previewResult || !locationObj || !locationObj.lat || !locationObj.lng) return;

        setPreviewResult(prev => {
            const cLat = prev.common_point?.lat;
            const cLng = prev.common_point?.lng;
            const distKm = (cLat && cLng) ? haversineKm(cLat, cLng, locationObj.lat, locationObj.lng) : 0;

            const updatePaxList = (list) => list.map(p => {
                if (p.temp_id === paxTempId) {
                    return {
                        ...p,
                        address: locationObj.address,
                        lat: locationObj.lat,
                        lng: locationObj.lng,
                        dist_to_common_km: distKm,
                        is_dubious_address: false
                    };
                }
                return p;
            });

            const updatedVehicles = prev.vehicles.map(v => ({
                ...v,
                passengers: updatePaxList(v.passengers)
            }));

            const updatedUnassigned = updatePaxList(prev.unassigned_passengers || []);

            // Actualizar selectedRouteVeh si es el que está en el Modal
            if (selectedRouteVeh) {
                const vehFound = updatedVehicles.find(v => v.vehicle_slot_id === selectedRouteVeh.vehicle_slot_id);
                if (vehFound) setSelectedRouteVeh(vehFound);
            }

            return {
                ...prev,
                vehicles: updatedVehicles,
                unassigned_passengers: updatedUnassigned,
                alerts: (prev.alerts || []).filter(al => !al.includes(`Fila ${paxTempId + 1}:`))
            };
        });

        setEditingPaxId(null);
        toast.success("Dirección corregida con éxito y coordenadas GPS actualizadas.");
    };

    // Re-procesamiento Rápido al Cambiar País
    const handleQuickReprocessCountry = (countryCode) => {
        setSelectedCountry(countryCode);
        toast.info(`Cambiando país a ${countryCode.toUpperCase()} y re-procesando borrador...`);
        setTimeout(() => {
            const processBtn = document.getElementById('btn-process-smart-bulk');
            if (processBtn) processBtn.click();
        }, 100);
    };

    // Confirmar Creación Final
    const handleConfirmBulk = async () => {
        if (!previewResult || !previewResult.vehicles || previewResult.vehicles.length === 0) {
            toast.warning("No hay vehículos agrupados para confirmar.");
            return;
        }

        setConfirming(true);
        try {
            const payload = {
                client_id: selectedClientId,
                requester_id: selectedRequesterId,
                area_id: selectedAreaId,
                operation_type: operationType,
                common_address: commonAddress,
                common_lat: commonCoords.lat,
                common_lng: commonCoords.lng,
                country_code: selectedCountry,
                vehicles: previewResult.vehicles,
                unassigned_passengers: previewResult.unassigned_passengers
            };

            const blobData = await confirmSmartBulk(payload);
            const blob = new Blob([blobData], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Consolidado_Reservas_Masivas_${new Date().toISOString().slice(0,10)}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);

            toast.success("¡Reservas masivas creadas con éxito! Planilla consolidada descargada.");
            setPreviewResult(null);
            setUploadedFile(null);
        } catch (err) {
            console.error("Error al confirmar reservas masivas:", err);
        } finally {
            setConfirming(false);
        }
    };

    return (
        <Container fluid className="py-4">
            {/* Header de la Sección */}
            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <div>
                    <h3 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                        <Sparkles className="text-primary" size={28} />
                        Carga Inteligente de Reservas
                    </h3>
                    <p className="text-muted small mb-0">
                        Generación automatizada y empaquetamiento geográfico inteligente de servicios consolidados.
                    </p>
                </div>
                <Button 
                    variant="outline-primary" 
                    className="d-flex align-items-center gap-2 font-weight-bold shadow-sm"
                    onClick={handleDownloadTemplate}
                >
                    <Download size={18} />
                    Descargar Plantilla Modelo (Excel)
                </Button>
            </div>

            <Row className="g-4">
                {/* Panel de Parámetros Superiores */}
                <Col lg={5}>
                    <Card className="border-0 shadow-sm rounded-4">
                        <Card.Header className="bg-primary text-white py-3 rounded-top-4">
                            <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
                                <FileSpreadsheet size={20} />
                                1. Configuración de Parámetros
                            </h6>
                        </Card.Header>
                        <Card.Body className="p-4">
                            <Form onSubmit={handleProcessPreview}>
                                {/* Selección de Cliente */}
                                <Form.Group className="mb-3">
                                    <Form.Label className="small fw-bold text-dark">Cliente Corporativo *</Form.Label>
                                    <Form.Select 
                                        value={selectedClientId} 
                                        onChange={e => setSelectedClientId(e.target.value)}
                                        required
                                    >
                                        <option value="">Seleccionar Cliente...</option>
                                        {clients.map(c => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>

                                {/* País y Solicitante & Área */}
                                <Row className="g-2 mb-3">
                                    <Col md={12}>
                                        <Form.Label className="small fw-bold text-dark">País / Región de Geocodificación *</Form.Label>
                                        <Form.Select 
                                            value={selectedCountry}
                                            onChange={e => setSelectedCountry(e.target.value)}
                                            className="fw-bold"
                                        >
                                            <option value="cl">🇨🇱 Chile</option>
                                            <option value="pe">🇵🇪 Perú</option>
                                            <option value="ar">🇦🇷 Argentina</option>
                                            <option value="co">🇨🇴 Colombia</option>
                                        </Form.Select>
                                    </Col>
                                </Row>

                                <Row className="g-2 mb-3">
                                    <Col md={6}>
                                        <Form.Label className="small fw-bold text-dark">Solicitante</Form.Label>
                                        <Form.Select 
                                            value={selectedRequesterId} 
                                            onChange={e => setSelectedRequesterId(e.target.value)}
                                            disabled={!selectedClientId}
                                        >
                                            <option value="">(Opcional)</option>
                                            {requesters.map(r => (
                                                <option key={r.id} value={r.id}>{r.full_name}</option>
                                            ))}
                                        </Form.Select>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Label className="small fw-bold text-dark">Área Operativa</Form.Label>
                                        <Form.Select 
                                            value={selectedAreaId} 
                                            onChange={e => setSelectedAreaId(e.target.value)}
                                            disabled={!selectedClientId}
                                        >
                                            <option value="">(Opcional)</option>
                                            {areas.map(a => (
                                                <option key={a.id} value={a.id}>{a.name}</option>
                                            ))}
                                        </Form.Select>
                                    </Col>
                                </Row>

                                {/* Tipo de Operación */}
                                <Form.Group className="mb-3">
                                    <Form.Label className="small fw-bold text-dark d-block">Tipo de Operación *</Form.Label>
                                    <div className="btn-group w-100" role="group">
                                        <Button 
                                            type="button"
                                            variant={operationType === 'zarpe' ? 'primary' : 'outline-primary'}
                                            onClick={() => setOperationType('zarpe')}
                                            className="py-2"
                                        >
                                            🚀 Zarpe
                                        </Button>
                                        <Button 
                                            type="button"
                                            variant={operationType === 'recogida' ? 'primary' : 'outline-primary'}
                                            onClick={() => setOperationType('recogida')}
                                            className="py-2"
                                        >
                                            📍 Recogida
                                        </Button>
                                    </div>
                                </Form.Group>

                                {/* Dirección Punto Común */}
                                <Form.Group className="mb-3">
                                    <Form.Label className="small fw-bold text-dark d-flex align-items-center gap-1">
                                        <MapPin size={16} className="text-primary" />
                                        Dirección del Punto Común * ({operationType === 'zarpe' ? 'Origen' : 'Destino'})
                                    </Form.Label>
                                    <AddressAutocomplete 
                                        value={commonAddress}
                                        onChange={setCommonAddress}
                                        onSelectLocation={(loc) => {
                                            setCommonAddress(loc.address);
                                            setCommonCoords({ lat: loc.lat, lng: loc.lng });
                                        }}
                                        placeholder="Ej. Aeropuerto SCL / Oficina Principal..."
                                    />
                                </Form.Group>

                                {/* Tolerancia Horaria */}
                                <Form.Group className="mb-3">
                                    <Form.Label className="small fw-bold text-dark d-flex align-items-center gap-1">
                                        <Clock size={16} className="text-muted" />
                                        Tolerancia Horaria de Agrupamiento
                                    </Form.Label>
                                    <Form.Select 
                                        value={toleranceMinutes} 
                                        onChange={e => setToleranceMinutes(Number(e.target.value))}
                                    >
                                        <option value={15}>15 Minutos</option>
                                        <option value={30}>30 Minutos (Recomendado)</option>
                                        <option value={45}>45 Minutos</option>
                                        <option value={60}>60 Minutos</option>
                                    </Form.Select>
                                </Form.Group>

                                {/* Mix de Vehículos a Usar */}
                                <Form.Group className="mb-3">
                                    <Form.Label className="small fw-bold text-dark d-flex align-items-center gap-1">
                                        <Car size={16} className="text-primary" />
                                        Mix de Vehículos Requeridos
                                    </Form.Label>
                                    <div className="bg-light p-3 rounded border" style={{ maxHeight: '240px', overflowY: 'auto' }}>
                                        {vehicleMix.map(v => (
                                            <div key={v.type} className="d-flex justify-content-between align-items-center mb-2 last-mb-0">
                                                <span className="small font-weight-bold text-dark">{v.name}</span>
                                                <div className="d-flex align-items-center gap-2">
                                                    <Button 
                                                        type="button"
                                                        size="sm" 
                                                        variant="outline-secondary"
                                                        onClick={() => handleVehicleCountChange(v.type, -1)}
                                                        disabled={v.count === 0}
                                                        className="px-2 py-0"
                                                    >-</Button>
                                                    <span className="fw-bold px-2">{v.count}</span>
                                                    <Button 
                                                        type="button"
                                                        size="sm" 
                                                        variant="outline-secondary"
                                                        onClick={() => handleVehicleCountChange(v.type, 1)}
                                                        className="px-2 py-0"
                                                    >+</Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </Form.Group>

                                {/* Archivo Excel/CSV */}
                                <Form.Group className="mb-4">
                                    <Form.Label className="small fw-bold text-dark mb-0">Planilla de Pasajeros (Excel / CSV) *</Form.Label>
                                    <Form.Control 
                                        type="file" 
                                        accept=".xlsx, .xls, .csv"
                                        onChange={e => setUploadedFile(e.target.files[0])}
                                        required
                                    />
                                </Form.Group>

                                <Button 
                                    id="btn-process-smart-bulk"
                                    type="submit" 
                                    variant="primary" 
                                    className="w-100 py-2.5 fw-bold shadow-sm d-flex justify-content-center align-items-center gap-2"
                                    disabled={processingPreview}
                                >
                                    {processingPreview ? (
                                        <>
                                            <Spinner animation="border" size="sm" />
                                            Procesando...
                                        </>
                                    ) : (
                                        <>
                                            <RefreshCw size={18} />
                                            Generar Previsualización Inteligente
                                        </>
                                    )}
                                </Button>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Panel de Previsualización Interactivo */}
                <Col lg={7}>
                    {previewResult ? (
                        <Card className="border-0 shadow-sm rounded-4">
                            <Card.Header className="bg-dark text-white py-3 rounded-top-4 d-flex justify-content-between align-items-center">
                                <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
                                    <CheckCircle2 size={20} className="text-success" />
                                    2. Borrador de Servicios Agrupados ({previewResult.vehicles.length} Vehículos)
                                </h6>
                                <Badge bg="info">{previewResult.total_passengers} Pasajeros Total</Badge>
                            </Card.Header>
                            <Card.Body className="p-4">
                                {/* Alertas de Direcciones Dudosas y Cambio Rápido de País */}
                                {previewResult.alerts && previewResult.alerts.length > 0 && (
                                    <Alert variant="warning" className="small mb-4 border-warning shadow-sm">
                                        <div className="fw-bold mb-2 d-flex flex-wrap align-items-center justify-content-between gap-2">
                                            <span className="d-flex align-items-center gap-1 text-dark">
                                                <AlertTriangle size={16} className="text-warning" /> Alertas de Verificación de Direcciones:
                                            </span>
                                            <div className="d-flex align-items-center gap-1 flex-wrap">
                                                <span className="text-muted small">¿País incorrecto?:</span>
                                                <Button size="sm" variant="outline-dark" className="py-0 px-2 small bg-white" onClick={() => handleQuickReprocessCountry('pe')}>
                                                    🔄 Re-procesar como 🇵🇪 Perú
                                                </Button>
                                                <Button size="sm" variant="outline-dark" className="py-0 px-2 small bg-white" onClick={() => handleQuickReprocessCountry('cl')}>
                                                    🔄 Re-procesar como 🇨🇱 Chile
                                                </Button>
                                                <Button size="sm" variant="outline-dark" className="py-0 px-2 small bg-white" onClick={() => handleQuickReprocessCountry('ar')}>
                                                    🔄 Re-procesar como 🇦🇷 Argentina
                                                </Button>
                                            </div>
                                        </div>
                                        <ul className="mb-0 ps-3">
                                            {previewResult.alerts.map((al, idx) => (
                                                <li key={idx}>{al}</li>
                                            ))}
                                        </ul>
                                    </Alert>
                                )}

                                {/* Lista de Vehículos Agrupados */}
                                <h6 className="fw-bold text-dark mb-3">Vehículos Asignados, Ruteo e Itinerario:</h6>
                                {previewResult.vehicles.map((v) => {
                                    const maxDistKm = Math.max(...v.passengers.map(p => p.dist_to_common_km || 0), 0);
                                    return (
                                        <Card key={v.vehicle_slot_id} className="border border-primary border-opacity-25 mb-4 bg-light rounded-3 shadow-none">
                                            <Card.Header className="bg-primary bg-opacity-10 py-2 d-flex justify-content-between align-items-center">
                                                <span className="fw-bold text-primary d-flex align-items-center gap-2">
                                                    🚗 Vehículo #{v.vehicle_slot_id} — {v.type} ({v.passengers.length}/{v.capacity} Pax)
                                                    <Badge bg="secondary" className="fw-normal">📏 {maxDistKm.toFixed(1)} km máx</Badge>
                                                    {v.passengers.length > v.capacity && (
                                                        <Badge bg="danger">Sobrecupo ({v.passengers.length}/{v.capacity})</Badge>
                                                    )}
                                                </span>
                                                <div className="d-flex align-items-center gap-2">
                                                    <Button 
                                                        size="sm"
                                                        variant="primary"
                                                        className="py-0 fw-bold d-flex align-items-center gap-1 shadow-sm"
                                                        onClick={() => setSelectedRouteVeh(v)}
                                                    >
                                                        🗺️ Ver Mapa de Ruta
                                                    </Button>
                                                    {v.passengers.length > 1 && (
                                                        <Button 
                                                            size="sm" 
                                                            variant="outline-secondary" 
                                                            className="py-0 fw-bold"
                                                            onClick={() => handleSplitVehicle(v.vehicle_slot_id)}
                                                        >
                                                            ✂️ Dividir
                                                        </Button>
                                                    )}
                                                </div>
                                            </Card.Header>
                                            <Card.Body className="p-0">
                                                {/* Resumen del Itinerario Calculado */}
                                                {v.itinerary && (
                                                    <div className="bg-white p-2.5 px-3 border-bottom small d-flex justify-content-between align-items-center text-muted">
                                                        <div>
                                                            <strong>📍 Origen:</strong> {v.itinerary.origin} ➔ 
                                                            {v.itinerary.waypoints && v.itinerary.waypoints.length > 0 && (
                                                                <span> <strong>Paradas:</strong> {v.itinerary.waypoints.length} paradas ➔ </span>
                                                            )}
                                                            <strong> 🏁 Destino:</strong> {v.itinerary.destination}
                                                        </div>
                                                        <Badge bg="info" text="dark">~{maxDistKm.toFixed(1)} km</Badge>
                                                    </div>
                                                )}

                                                <Table hover size="sm" responsive className="mb-0 small align-middle">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>Seq</th>
                                                            <th>Pasajero</th>
                                                            <th>Área</th>
                                                            <th>Dirección / Comuna</th>
                                                            <th>Distancia Punto Común</th>
                                                            <th>Reasignar a</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {v.passengers.map((p, pIdx) => (
                                                            <tr key={p.temp_id || pIdx}>
                                                                <td className="fw-bold text-muted">#{pIdx + 1}</td>
                                                                <td className="fw-bold text-dark">{p.name}</td>
                                                                <td>{p.area || '-'}</td>
                                                                <td>
                                                                    {editingPaxId === p.temp_id ? (
                                                                        <div style={{ minWidth: '260px' }}>
                                                                            <AddressAutocomplete
                                                                                onSelectLocation={(loc) => handleSaveCorrectedAddress(p.temp_id, loc)}
                                                                                placeholder="Buscar dirección con Google Maps..."
                                                                            />
                                                                            <Button size="sm" variant="link" className="text-danger p-0 text-decoration-none mt-1" onClick={() => setEditingPaxId(null)}>
                                                                                Cancelar
                                                                            </Button>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="d-flex justify-content-between align-items-center gap-2">
                                                                            <div>
                                                                                <div>{p.address}</div>
                                                                                <small className="text-muted">{p.comuna || ''}</small>
                                                                            </div>
                                                                            <Button 
                                                                                size="sm" 
                                                                                variant="outline-warning" 
                                                                                className="py-0 px-1 text-dark flex-shrink-0"
                                                                                title="Buscar y corregir dirección en Google Maps"
                                                                                onClick={() => setEditingPaxId(p.temp_id)}
                                                                            >
                                                                                ✏️
                                                                            </Button>
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <Badge bg="light" text="dark" className="border fw-bold">
                                                                        {p.dist_to_common_km ? `${p.dist_to_common_km.toFixed(1)} km` : '-'}
                                                                    </Badge>
                                                                </td>
                                                                <td>
                                                                    <Form.Select 
                                                                        size="sm"
                                                                        className="py-0 px-1 small border-primary border-opacity-50"
                                                                        value={v.vehicle_slot_id}
                                                                        onChange={(e) => handleMovePassenger(p.temp_id, e.target.value)}
                                                                    >
                                                                        <option value={v.vehicle_slot_id}>Vehículo #{v.vehicle_slot_id}</option>
                                                                        {previewResult.vehicles
                                                                            .filter(otherV => otherV.vehicle_slot_id !== v.vehicle_slot_id)
                                                                            .map(otherV => (
                                                                                <option key={otherV.vehicle_slot_id} value={otherV.vehicle_slot_id}>
                                                                                    Mover a Vehículo #{otherV.vehicle_slot_id} ({otherV.passengers.length}/{otherV.capacity})
                                                                                </option>
                                                                            ))}
                                                                        <option value="unassigned">Desasignar Pasajero</option>
                                                                    </Form.Select>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </Card.Body>
                                        </Card>
                                    );
                                })}

                                {/* Pasajeros Sin Asignar */}
                                {previewResult.unassigned_passengers && previewResult.unassigned_passengers.length > 0 && (
                                    <Card className="border border-danger border-opacity-25 mb-4 bg-danger bg-opacity-10 rounded-3">
                                        <Card.Header className="bg-danger text-white py-2 fw-bold d-flex justify-content-between align-items-center">
                                            <span>⚠️ Pasajeros Sin Asignar ({previewResult.unassigned_passengers.length}):</span>
                                        </Card.Header>
                                        <Card.Body className="p-3">
                                            <Table hover size="sm" responsive className="mb-0 small align-middle bg-white rounded">
                                                <thead>
                                                    <tr>
                                                        <th>Pasajero</th>
                                                        <th>Dirección</th>
                                                        <th>Asignar a Vehículo</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {previewResult.unassigned_passengers.map((up) => (
                                                        <tr key={up.temp_id}>
                                                            <td className="fw-bold">{up.name}</td>
                                                            <td>{up.address}</td>
                                                            <td>
                                                                <Form.Select 
                                                                    size="sm"
                                                                    className="py-0 px-1 small text-success fw-bold"
                                                                    value="unassigned"
                                                                    onChange={(e) => handleMovePassenger(up.temp_id, e.target.value)}
                                                                >
                                                                    <option value="unassigned">Sin Asignar</option>
                                                                    {previewResult.vehicles.map(v => (
                                                                        <option key={v.vehicle_slot_id} value={v.vehicle_slot_id}>
                                                                            Asignar a Vehículo #{v.vehicle_slot_id} ({v.passengers.length}/{v.capacity})
                                                                        </option>
                                                                    ))}
                                                                </Form.Select>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </Card.Body>
                                    </Card>
                                )}

                                {/* Botón de Confirmación */}
                                <Button 
                                    variant="success" 
                                    size="lg" 
                                    className="w-100 py-3 fw-bold shadow d-flex justify-content-center align-items-center gap-2"
                                    onClick={handleConfirmBulk}
                                    disabled={confirming}
                                >
                                    {confirming ? (
                                        <>
                                            <Spinner animation="border" size="sm" />
                                            Creando Reservas y Generando Excel...
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle2 size={20} />
                                            Confirmar Generación y Descargar Consolidado
                                        </>
                                    )}
                                </Button>
                            </Card.Body>
                        </Card>
                    ) : (
                        <Card className="border-0 shadow-sm rounded-4 text-center py-5 bg-light">
                            <Card.Body className="py-5">
                                <FileSpreadsheet size={48} className="text-muted mb-3 opacity-50" />
                                <h5 className="fw-bold text-dark mb-2">Esperando Configuración</h5>
                                <p className="text-muted small mx-auto" style={{ maxWidth: '400px' }}>
                                    Completa los parámetros en el panel izquierdo y adjunta el archivo Excel/CSV para calcular el empaquetamiento inteligente.
                                </p>
                            </Card.Body>
                        </Card>
                    )}
                </Col>
            </Row>

            {/* Modal de Mapa Interactivo de la Ruta con Drag & Drop y Demora */}
            <Modal 
                show={!!selectedRouteVeh} 
                onHide={() => setSelectedRouteVeh(null)} 
                size="xl" 
                centered
                className="rounded-4"
            >
                <Modal.Header closeButton className="bg-primary text-white py-3">
                    <Modal.Title className="h6 fw-bold mb-0 d-flex flex-wrap align-items-center gap-2">
                        <span>🗺️ Ruta del Vehículo #{selectedRouteVeh?.vehicle_slot_id} — {selectedRouteVeh?.type} ({selectedRouteVeh?.passengers.length} Pax)</span>
                        {routeStats.durationMins > 0 && (
                            <Badge bg="warning" text="dark" className="fw-bold fs-7 shadow-sm ms-auto me-3">
                                ⏱️ Demora Est.: {formatDuration(routeStats.durationMins)} ({routeStats.distKm.toFixed(1)} km)
                            </Badge>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-3 bg-light">
                    {selectedRouteVeh && (() => {
                        const validPax = (selectedRouteVeh.passengers || []).filter(p => p.lat && p.lng);
                        let pickupLoc = null;
                        let dropoffLoc = null;
                        let stopsLocs = [];

                        if (operationType === 'zarpe') {
                            pickupLoc = previewResult?.common_point?.lat ? { lat: Number(previewResult.common_point.lat), lng: Number(previewResult.common_point.lng) } : null;
                            if (validPax.length > 0) {
                                dropoffLoc = { lat: Number(validPax[validPax.length - 1].lat), lng: Number(validPax[validPax.length - 1].lng) };
                                stopsLocs = validPax.slice(0, -1).map(p => ({ lat: Number(p.lat), lng: Number(p.lng) }));
                            }
                        } else {
                            // Recogida: Origen (Punto A) = Primer Pasajero, Paradas = Pasajeros 2 en adelante, Destino = Punto Común
                            if (validPax.length > 0) {
                                pickupLoc = { lat: Number(validPax[0].lat), lng: Number(validPax[0].lng) };
                                stopsLocs = validPax.slice(1).map(p => ({ lat: Number(p.lat), lng: Number(p.lng) }));
                                dropoffLoc = previewResult?.common_point?.lat 
                                    ? { lat: Number(previewResult.common_point.lat), lng: Number(previewResult.common_point.lng) } 
                                    : { lat: Number(validPax[validPax.length - 1].lat), lng: Number(validPax[validPax.length - 1].lng) };
                            }
                        }

                        return (
                            <Row className="g-3">
                                {/* Columna Izquierda: Lista de Paradas con Drag & Drop y ⬆️/⬇️ */}
                                <Col lg={5}>
                                    <Card className="border-0 shadow-sm rounded-3 h-100">
                                        <Card.Header className="bg-white py-2 fw-bold small text-dark d-flex justify-content-between align-items-center">
                                            <span>🔀 Orden de Recogida (Arrastra para mover):</span>
                                            <Badge bg="primary">{selectedRouteVeh.passengers.length} Pasajeros</Badge>
                                        </Card.Header>
                                        <Card.Body className="p-2" style={{ maxHeight: '480px', overflowY: 'auto' }}>
                                            {/* Cabecera del Origen en Zarpe */}
                                            {operationType === 'zarpe' && (
                                                <div className="text-primary fw-bold small mb-2 p-2 bg-primary bg-opacity-10 rounded border">
                                                    🔴 <strong>Punto A (Origen General):</strong> {selectedRouteVeh.itinerary?.origin}
                                                </div>
                                            )}

                                            {/* Secuencia de Paradas Reordenables por Drag & Drop */}
                                            {selectedRouteVeh.passengers.map((p, pIdx) => {
                                                const markerLetter = String.fromCharCode(65 + (operationType === 'zarpe' ? pIdx + 1 : pIdx));
                                                const labelMarker = `Punto ${markerLetter}`;

                                                return (
                                                    <div 
                                                        key={p.temp_id || pIdx} 
                                                        draggable={true}
                                                        onDragStart={(e) => {
                                                            setDraggedIndex(pIdx);
                                                            e.dataTransfer.effectAllowed = 'move';
                                                        }}
                                                        onDragOver={(e) => {
                                                            e.preventDefault();
                                                            e.dataTransfer.dropEffect = 'move';
                                                        }}
                                                        onDrop={(e) => {
                                                            e.preventDefault();
                                                            if (draggedIndex !== null && draggedIndex !== pIdx) {
                                                                handleReorderPassengerDrag(selectedRouteVeh.vehicle_slot_id, draggedIndex, pIdx);
                                                                setDraggedIndex(null);
                                                            }
                                                        }}
                                                        className={`p-2 mb-2 bg-white rounded border ${draggedIndex === pIdx ? 'border-primary border-2 shadow' : 'border-secondary border-opacity-25 shadow-sm'} d-flex justify-content-between align-items-center`}
                                                        style={{ cursor: 'grab' }}
                                                    >
                                                        <div className="d-flex align-items-center me-2 overflow-hidden">
                                                            <span className="me-2 text-muted fw-bold" style={{ cursor: 'grab' }} title="Arrastra para reordenar">
                                                                ⋮⋮
                                                            </span>
                                                            <div className="overflow-hidden">
                                                                <div className="fw-bold small text-dark truncate">
                                                                    {labelMarker}: {p.name}
                                                                </div>
                                                                <div className="text-muted small truncate" style={{ fontSize: '0.75rem' }}>
                                                                    {p.address} ({p.comuna || ''})
                                                                </div>
                                                                {p.dist_to_common_km && (
                                                                    <Badge bg="light" text="dark" className="border mt-1" style={{ fontSize: '0.7rem' }}>
                                                                        {p.dist_to_common_km.toFixed(1)} km al Pto Común
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="d-flex flex-column gap-1 flex-shrink-0">
                                                            <Button 
                                                                size="sm" 
                                                                variant="outline-secondary" 
                                                                className="py-0 px-1 font-weight-bold"
                                                                style={{ fontSize: '0.7rem' }}
                                                                onClick={() => handleReorderPassenger(selectedRouteVeh.vehicle_slot_id, pIdx, -1)}
                                                                disabled={pIdx === 0}
                                                            >
                                                                ⬆️
                                                            </Button>
                                                            <Button 
                                                                size="sm" 
                                                                variant="outline-secondary" 
                                                                className="py-0 px-1 font-weight-bold"
                                                                style={{ fontSize: '0.7rem' }}
                                                                onClick={() => handleReorderPassenger(selectedRouteVeh.vehicle_slot_id, pIdx, 1)}
                                                                disabled={pIdx === selectedRouteVeh.passengers.length - 1}
                                                            >
                                                                ⬇️
                                                            </Button>
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            {/* Destino Final */}
                                            <div className="text-success fw-bold small mt-2 p-2 bg-success bg-opacity-10 rounded border">
                                                🏁 <strong>Destino Final de la Ruta:</strong> {operationType === 'recogida' ? (selectedRouteVeh.itinerary?.destination || commonAddress) : selectedRouteVeh.passengers[selectedRouteVeh.passengers.length - 1]?.address}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>

                                {/* Columna Derecha: Canvas del Mapa en Vivo */}
                                <Col lg={7}>
                                    <Card className="border-0 shadow-sm rounded-3 h-100">
                                        <Card.Body className="p-2">
                                            <div style={{ width: '100%', height: '480px', borderRadius: '8px', overflow: 'hidden' }}>
                                                <RouteMap
                                                    key={`${selectedRouteVeh.vehicle_slot_id}-${selectedRouteVeh.passengers.map(p => p.temp_id).join('-')}`}
                                                    pickupLocation={pickupLoc}
                                                    dropoffLocation={dropoffLoc}
                                                    stops={stopsLocs}
                                                    onDistanceCalculated={(distKm, durationMins) => {
                                                        setRouteStats({ distKm, durationMins });
                                                    }}
                                                />
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        );
                    })()}
                </Modal.Body>
                <Modal.Footer className="py-2 bg-white">
                    {selectedRouteVeh?.route_url && (
                        <a 
                            href={selectedRouteVeh.route_url} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="btn btn-sm btn-outline-primary fw-bold"
                        >
                            Abrir en Google Maps Externo ↗
                        </a>
                    )}
                    <Button variant="secondary" size="sm" onClick={() => setSelectedRouteVeh(null)}>
                        Cerrar Ventana
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
}
